import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, Input, Label } from '@shared/ui';
import { useApiServices } from '@shared/lib/use-api-services';
import { getApiErrorMessage } from '@shared/lib/get-api-error-message';
import type { SecondFactor, SecondFactorMethod } from '@shared/api/contracts/security';

interface SecondFactorDialogProps {
  open: boolean;
  purpose: string;
  methods: SecondFactorMethod[];
  invalid?: boolean;
  sendEmail?: () => Promise<void>;
  onSubmit: (factor: SecondFactor) => void;
  onCancel: () => void;
}

const METHOD_KEYS: Record<SecondFactorMethod, string> = {
  totp: 'security.factor.totp',
  email: 'security.factor.email',
  backup: 'security.factor.backup',
};

export function SecondFactorDialog({
  open,
  purpose,
  methods,
  invalid,
  sendEmail: sendBoundEmail,
  onSubmit,
  onCancel,
}: SecondFactorDialogProps) {
  const { t } = useTranslation('cabinet');
  const services = useApiServices();
  const [method, setMethod] = useState<SecondFactorMethod>(methods[0] ?? 'totp');
  const [code, setCode] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setMethod(methods[0] ?? 'totp');
    setCode('');
    setSentTo(null);
    setError(invalid ? t('security.factor.invalid') : null);
  }, [invalid, methods, open, t]);

  const sendEmail = async () => {
    setSending(true);
    setError(null);
    try {
      if (sendBoundEmail) {
        await sendBoundEmail();
        setSentTo(t('security.factor.emailSent'));
        return;
      }
      const response = await services.security.sendEmailOtp(purpose);
      setSentTo(response.sentTo ? t('security.factor.sentTo', { email: response.sentTo }) : t('security.factor.emailSent'));
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, t, 'security.factor.sendFailed'));
    } finally {
      setSending(false);
    }
  };

  const submit = () => {
    const trimmed = code.trim();
    if (method === 'backup' ? trimmed.length < 4 : trimmed.length < 6) {
      setError(t('security.factor.invalid'));
      return;
    }
    onSubmit({ method, code: trimmed });
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('security.factor.title')}</DialogTitle>
          <DialogDescription>{t('security.factor.description')}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          {methods.map((item) => (
            <Button
              key={item}
              type="button"
              size="sm"
              variant={item === method ? 'default' : 'secondary'}
              onClick={() => {
                setMethod(item);
                setCode('');
                setError(null);
              }}
            >
              {t(METHOD_KEYS[item])}
            </Button>
          ))}
        </div>
        {method === 'email' ? (
          <div className="space-y-2">
            <Button type="button" variant="outline" onClick={() => void sendEmail()} loading={sending}>
              {t('security.factor.sendEmail')}
            </Button>
            {sentTo ? <p className="text-sm text-muted-foreground">{sentTo}</p> : null}
          </div>
        ) : null}
        <div className="space-y-2">
          <Label htmlFor="second-factor-code">{t('security.factor.code')}</Label>
          <Input
            id="second-factor-code"
            value={code}
            autoComplete="one-time-code"
            onChange={(event) => {
              const next = event.target.value;
              setCode(next);
              if (method !== 'backup' && next.replace(/\D/g, '').length === 6) {
                onSubmit({ method, code: next.replace(/\D/g, '') });
              }
            }}
          />
          {error ? <p className="text-xs text-destructive">{error}</p> : null}
        </div>
        <Button type="button" className="w-full" variant="default" onClick={submit}>
          {t('security.factor.confirm')}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
